<template>
  <div>
    <v-row v-if='interview.welcomingText'>
      <v-col cols='12'>
        <h1>{{ $i18n.t('InterviewPage.title') }}</h1>
        <div v-html='interview.welcomingText'/>
      </v-col>
      <v-col cols='12' v-if='interview.freeText'>
        <interview-free-text :text='interview.freeText'/>
      </v-col>
      <v-col cols='12' v-if='interview.hasNote'>
        <interview-note
          :note='getPage()'
          :current-page='0'
          :key='`n_${interview.id}_topLevel`'
          :editor-id='iNote-0'
          @sync='(val) => $emit("sync", val)'/>
      </v-col>
    </v-row>
    <template v-if='interviewCompetencies'>
      <v-card class='mb-4' v-for='(ic, icIndex) in interviewCompetencies' :key='`${ic.interviewId}_${ic.exerciseCompetency.id}_${icIndex}`'>
        <v-card-title>{{ ic.text }}</v-card-title>
        <v-card-subtitle>
          <span v-if='ic.exerciseCompetency.competency.description'>{{ ic.exerciseCompetency.competency.description }}</span>
          <span v-if='ic.exerciseCompetency.competency.description && ic.exerciseCompetency.competency.category'> - {{ ic.exerciseCompetency.competency.category }}</span>
        </v-card-subtitle>
        <v-card-text>
<!--          now do the iterating stuffs and value magic here-->
          <template v-if='ic.text !== "Subjektiver Gesamteindruck"'>
            <v-expansion-panels :key='`competency-panel-${ic.exerciseCompetency.id}`' multiple>
              <template v-if='ic.interviewQuestions && ic.interviewQuestions.length > 0'>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h4>{{$i18n.t('InterviewPage.QuestionSection')}}</h4>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-expansion-panels
                      :key='`questPanel_${question.interviewCompetencyId}_${ic.exerciseCompetency.id}_${question.id}_${qIndex}`'
                      v-for='(question, qIndex) in ic.interviewQuestions'>
                      <v-expansion-panel :key='`quest_${question.interviewCompetencyId}_${ic.exerciseCompetency.id}_${question.id}_${qIndex}`' class='mb-2'>
                        <v-expansion-panel-header>
                          <h4>{{ question.text ? question.text : `${$i18n.t('InterviewPage.questionLabel')} ${qIndex + 1}`}}</h4>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <interview-free-text
                            v-if='question.hasFreeText'
                            :key='`ft_${ic.exerciseCompetency.id}_${question.id}`'
                            :text='question.freeText'/>
                          <interview-note
                            v-if='question.hasNote'
                            :key='`n_${ic.exerciseCompetency.id}_${question.id}_qLevel-${qIndex}`'
                            :note='getPage(icIndex, qIndex)'
                            :current-page='qIndex'
                            :editor-id='`icNote-${icIndex}_qNote-${qIndex}`'/>
                          <interview-rating-card
                            v-if='(interviewScale.interviewCriterions || scale) && question.isScored && question.interviewCues.length > 0'
                            :key='`assessment_${ic.exerciseCompetency.id}_${question.id}`'
                            :criterions='interviewScale.interviewCriterions'
                            :cues='question.interviewCues'
                            :scale='interviewScale'
                            :assessment='getTempAssessment(ic, question)'
                            class='mt-2'
                            @ratingChanged='(val) => updateTmpAssessment(val)'
                            @ki-cue-toggle='(val) => updateTmpCue(val)'
                            />
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-spacer/>
              </template>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h4>{{ $i18n.t('InterviewPage.OverallCompetencySection') }}</h4>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <interview-free-text v-if='ic.hasFreeText' :text='ic.freeText'/>
                    <interview-note v-if='ic.hasNote'
                                    :note='getPage(icIndex)'
                                    :key='`_icLevel-${icIndex}`'
                                    :current-page='icIndex'
                                    :editor-id='`icNote-${icIndex}`'
                                    @sync='(val) => $emit("sync", val)'/>
                    <interview-rating-card
                      v-if='scale'
                      :key='`assessment_${ic.interviewId}_${ic.exerciseCompetency.id}`'
                      :criterions='scale.criterions'
                      :assessment='getAssessment(icIndex, ic)'
                      :cues='ic.exerciseCompetency.behavioralAnchors'
                      :scale='scale'
                      class='mt-2'
                      @ratingChanged='(val) => updateAssessment(val)'
                      @ki-cue-toggle='(val) => updateCue(val)'/>
                  </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
          </template>
<!--          Subjective Overall Impression never has a freetext or note or any questions for that matter -->
<!--          Thats why we handle it as a seperate case here -->
          <template v-if='ic.text === "Subjektiver Gesamteindruck"'>
            <interview-rating-card
              v-if='scale'
              :key='`assessment_${ic.exerciseCompetency.id}_subjective_impression`'
              :criterions='scale.criterions'
              :assessment='getAssessment(icIndex, ic)'
              :cues='ic.exerciseCompetency.behavioralAnchors'
              :scale='scale'
              class='mt-2'
              @ratingChanged='(val) => {updateAssessment(val)}'
              @ki-cue-toggle='(val) => updateCue(val)'/>
          </template>
        </v-card-text>
      </v-card>
    </template>
    <v-row v-if='interview.closingText'>
      <v-col cols='12'>
        <h3>{{ $i18n.t('InterviewPage.EndingWords')}}</h3>
        <div v-html='interview.closingText'/>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import InterviewNote from '@/components/Interview/InterviewNote.vue';
import InterviewFreeText from '@/components/Interview/InterviewFreeText.vue';
import InterviewRatingCard from '@/components/Interview/InterviewRatingCard.vue';
import { defaultAssessment, defaultTmpAssessment } from '@/libs/commonHelper';
import { v4 as uuid } from 'uuid';
import BusinessLogicObservation from '@/libs/businessLogicObservation';
import ACTIONS from '@/storeActions';

export default {
  name: 'Interview',
  components: {
    InterviewNote,
    InterviewFreeText,
    InterviewRatingCard
  },
  props: {
    interview: {
      type: Object,
      required: true
    },
    interviewCompetencies: {
      type: Array,
      required: true
    },
    participant: {
      type: Object,
      required: true
    },
    exercise: {
      type: Object,
      required: true
    },
    observations: {
      type: Array,
      required: true
    },
    assessments: {
      type: Array
    },
    tmpAssessments: {
      type: Array
    },
    interviewScale: {
      type: Object,
      required: true
    },
    noteCount: {
      type: Number,
      required: true
    },
    noteMap: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters(['currentObserver', 'syncFromAppToCockpitAllowed', 'isGuestObserver']),
    ...mapState({
      // Fallback
      scale: (state) => state.runInfo.scale,
    })
  },
  data: () => ({
    logic: undefined,
    pageIndex: 0,
    loaded: false
  }),
  methods: {
    getRating(icIndex, ic) {
      return ic.rating
    },
    getAssessment(icIndex, ic) {
      return this.assessments.find((a) => a?.competencyExerciseId === ic.exerciseCompetency.id) || defaultAssessment(this.participant.id, ic.exerciseCompetency.id, this.currentObserver.id)
    },
    getTempAssessment(ic, q) {
      return this.tmpAssessments.find((a) => a?.competencyExerciseId === ic.exerciseCompetency.id && a.participantId === this.participant.id && a.questionId === q.id) || defaultTmpAssessment(this.participant.id, ic.exerciseCompetency.id, q.id, this.currentObserver.id)
    },
    getPage(icIndex, iqIndex) {
      if (this.noteMap) {
        if ( typeof icIndex !== 'number' ) return this.observations.find((x) => x.page === this.noteMap.topLevel.page) || {};
        let map;
        if ( typeof iqIndex !== 'number' ) map = this.noteMap.icLevel.find((i) => i.index === icIndex)
        if ( typeof iqIndex === 'number' ) map = this.noteMap.icLevel.find((i) => i.index === icIndex && i.qIndex === iqIndex)
        if ( typeof map?.page === 'number' ) return this.observations.find((x) => x.page === map.page) || {}
        return {}
      }
      return {}
    },
    saveAssessment(assessment) {
      const self = this;
      self.$apiRequest[assessment.id ? 'put' : 'post']('/assessments', Array.isArray(assessment) ? assessment : [assessment])
        .then((data) => {
          self.$store.dispatch(ACTIONS.UPDATE_ASSESSMENTS, { current: Array.isArray(assessment) ? assessment : [assessment], response: data })
            .catch((error) => {
              console.log(error);
              self.$store.dispatch(ACTIONS.SET_SNACKBAR, { text: self.$t('Errors.unknown'), color: 'error' })
            })
        })
    },
    updateAssessment({ value, assessment }) {
      const payload = { ...assessment, modified: true };
      const self = this;
      payload.selectedValue = value;
      if (!payload.id && !payload.temporaryId) {
        payload.temporaryId = uuid();
      }
      if (!this.syncFromAppToCockpitAllowed || this.isGuestObserver) {
        console.log('Skipping sync - not allowed currently');
        const update = Array.isArray(payload) ? payload : [payload];
        self.$store.dispatch(ACTIONS.UPDATE_ASSESSMENTS, { current: update, response: update })
        return;
      }
      this.saveAssessment(payload)
    },
    updateTmpAssessment({ value, assessment }) {
      const payload = { ...assessment, modified: true };
      const self = this;
      payload.selectedValue = value;
      if (!payload.id) payload.id = uuid();
      self.$store.dispatch(ACTIONS.UPDATE_TMP_ASSESSMENT, payload)
    },
    updateCue({ cueId, assessment }) {
      const payload = { ...assessment, modified: true };
      if (!payload.selectedCuesIds) payload.selectedCuesIds = [];
      if (payload.selectedCuesIds.includes(cueId)) {
        payload.selectedCuesIds = payload.selectedCuesIds.filter((cue) => cue !== cueId);
      } else {
        payload.selectedCuesIds.push(cueId);
      }
      if (payload.id || payload.selectedValue) {
        this.saveAssessment(payload)
        return;
      }
      this.$store.dispatch(ACTIONS.SET_SINGLE_ASSESSMENT, payload);
    },
    updateTmpCue({ cueId, assessment }) {
      const payload = { ...assessment };
      if (!payload.selectedCuesIds) payload.selectedCuesIds = [];
      if (payload.selectedCuesIds.includes(cueId)) payload.selectedCuesIds = payload.selectedCuesIds.filter((cue) => cue !== cueId)
      else payload.selectedCuesIds.push(cueId);
      if (!payload.id) payload.id = uuid();
      this.$store.dispatch(ACTIONS.UPDATE_TMP_ASSESSMENT, payload)
    },
  },
  mounted() {
    const self = this;
    this.logic = new BusinessLogicObservation(this);
    this.loaded = true;
  }
}
</script>

<style scoped>

</style>
